import React, { useMemo, useState } from "react"
import styles from "@/styles/Manage.module.scss"
import modalStyles from "@/styles/Modal.module.css"
import buttonStyles from "@/styles/Buttons.module.scss"
import { Box, Modal } from "@mui/material"
import utilityStyles from "@/styles/UtilityStyles.module.scss"
import Colors from "@/styles/Colors.module.scss"
import Dropdown from "@/components/common/Dropdown"
import WaitlistEntryModalTitle from "../waitlist/WaitlistEntryModal/WaitlistEntryModalTitle"
import ManageListEntryAction from "@/components/pages/manage/ManageListEntryAction"
import classNames from "classnames"
import DragReorderItem from '@/components/common/DragReorder/DragReorderItem'
import { ManageListEntryProps, ManageListEntryActionProps } from './interface'

function ManageListEntry({ actions, item, classes }: ManageListEntryProps) {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  const [confirmDeactivateModal, setConfirmDeactivateModal] = useState(false)
  const [confirmReactivateModal, setConfirmReactivateModal] = useState(false)
  const [confirmDeleteModal, setConfirmDeleteModal] = useState(false)

  const {
    id,
    detailStatusComponent,
    name,
    canDeactivate,
    canDelete,
    canEdit,
    disableEdit,
    deactivated,
    details,
    iconSrc,
    readOnly,
  } = item

  function editItemAction() {
    actions.editItem(id)
    setIsDropdownOpen(false)
  }

  function deactivateItemAction() {
    setConfirmDeactivateModal(true)
    setIsDropdownOpen(false)
  }

  function confirmDeactivate() {
    actions.deactivateItem(id)
    setConfirmDeactivateModal(false)
  }

  const cancelDeactivate = () => {
    setConfirmDeactivateModal(false)
  }

  function reactivateItemAction() {
    setConfirmReactivateModal(true)
    setIsDropdownOpen(false)
  }

  function confirmReactivate() {
    actions.reactivateItem(id)
    setConfirmReactivateModal(false)
  }

  const cancelReactivate = () => {
    setConfirmReactivateModal(false)
  }

  function deleteItemAction() {
    setConfirmDeleteModal(true)
    setIsDropdownOpen(false)
  }

  function confirmDelete() {
    actions.deleteItem(id)
    setConfirmDeleteModal(false)
  }

  const cancelDelete = () => {
    setConfirmDeleteModal(false)
  }

  const entryActions: Array<ManageListEntryActionProps> = useMemo(
    () =>
      [
        disableEdit ||
        (deactivated && (!!actions.deactivateItem || !!actions.reactivateItem))
          ? null
          : {
              iconSrc: '/icons/edit.svg',
              clickHandler: editItemAction,
              text: 'Edit',
              textStyle: { color: Colors['theme-slate'], fontWeight: 500 },
            },
        ,
        canDeactivate && actions.deactivateItem && !deactivated
          ? {
              iconSrc: '/icons/deactivate.svg',
              clickHandler: deactivateItemAction,
              text: 'Deactivate',
              textStyle: { color: Colors['theme-red'], fontWeight: 500 },
            }
          : null,
        canDeactivate && actions.reactivateItem && deactivated
          ? {
              iconSrc: '/icons/reactivate.svg',
              clickHandler: reactivateItemAction,
              text: 'Reactivate',
              textStyle: { color: Colors['theme-green'], fontWeight: 500 },
            }
          : null,
        canDelete && actions.deleteItem
          ? {
              iconSrc: '/icons/delete.svg',
              clickHandler: deleteItemAction,
              text: 'Delete',
              textStyle: { color: Colors['theme-red'], fontWeight: 500 },
            }
          : null,
      ].filter((x) => !!x),
    [actions]
  )

  return (
    <DragReorderItem value={item} id={id}>
      <div className={classNames(styles.entry, classes)}>
        <section
          className={classNames(
            styles.entryIconSection,
            deactivated && styles.entryDeactivated
          )}
        >
          <img src={iconSrc} alt="" />
        </section>
        <section
          className={classNames(
            styles.entryInfoSection,
            deactivated && styles.entryDeactivated
          )}
        >
          <h3
            style={{
              textOverflow: 'ellipsis',
              overflow: 'hidden',
              fontSize: 16,
              fontWeight: 700,
            }}
          >
            {name}
          </h3>
          <span>{details}</span>
        </section>
        <section className={styles.entryActionSection}>
          {detailStatusComponent}
          <Dropdown
            className="entry-action"
            isOpen={isDropdownOpen}
            setIsOpen={setIsDropdownOpen}
            triggerComponent={
              <img
                className="entry-action"
                src={`/icons/${readOnly ? 'dots-disabled' : 'dots'}.svg`}
                width="16px"
                alt=""
              />
            }
            disabled={readOnly}
          >
            {entryActions.map((x, i) => (
              <React.Fragment key={i}>
                {i > 0 && <div className={utilityStyles.separatorCondensed} />}
                <ManageListEntryAction {...x} />
              </React.Fragment>
            ))}
          </Dropdown>
        </section>
      </div>
      <Modal open={confirmDeactivateModal} onClose={cancelDeactivate}>
        <Box className={modalStyles.modalBox}>
          <WaitlistEntryModalTitle
            icon={'/icons/deactivate.svg'}
            title={`Deactivate`}
          />
          <p>
            Are you sure you want to deactivate <strong>{name}?</strong>
          </p>
          <br />
          <Box className={modalStyles.modalButtonRow}>
            <button
              className={classNames(buttonStyles.grayBtn)}
              onClick={cancelDeactivate}
            >
              Nevermind
            </button>
            <button
              className={classNames(buttonStyles.redBtn)}
              onClick={confirmDeactivate}
            >
              Deactivate
            </button>
          </Box>
        </Box>
      </Modal>
      <Modal open={confirmReactivateModal} onClose={cancelReactivate}>
        <Box className={modalStyles.modalBox}>
          <WaitlistEntryModalTitle
            icon={'/icons/reactivate.svg'}
            title={`Reactivate`}
          />
          <p>
            Are you sure you want to reactivate <strong>{name}?</strong>
          </p>
          <br />
          <Box className={modalStyles.modalButtonRow}>
            <button
              className={classNames(buttonStyles.grayBtn)}
              onClick={cancelReactivate}
            >
              Nevermind
            </button>
            <button
              className={classNames(buttonStyles.greenBtn)}
              onClick={confirmReactivate}
            >
              Reactivate
            </button>
          </Box>
        </Box>
      </Modal>
      <Modal open={confirmDeleteModal} onClose={cancelDelete}>
        <Box className={modalStyles.modalBox}>
          <WaitlistEntryModalTitle
            icon={'/icons/delete.svg'}
            title={`Delete`}
          />
          <p>
            Are you sure you want to delete <strong>{name}?</strong> This action
            can't be undone.
          </p>
          <br />
          <Box className={modalStyles.modalButtonRow}>
            <button
              className={classNames(buttonStyles.grayBtn)}
              onClick={cancelDelete}
            >
              Nevermind
            </button>
            <button
              className={classNames(buttonStyles.redBtn)}
              onClick={confirmDelete}
            >
              Delete
            </button>
          </Box>
        </Box>
      </Modal>
    </DragReorderItem>
  )
}

export default ManageListEntry
